import * as styles from "./Knob.css";

import { clsx } from "clsx";
import { useId, useRef, useState } from "react";
import type { JSX } from "react";

import { isSizePreset } from "@frontend/styles";
import { SIZES } from "@frontend/styles/constants";

import { KnobContext, makeKnobContext } from "./Knob.context";
import type { KnobProps } from "./Knob.types";
import { getKnobSizeDimensions } from "./constants";

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function getDecimalPrecision(value: number): number {
  const asString = value.toString();
  const dotIndex = asString.indexOf(".");

  return dotIndex === -1 ? 0 : asString.length - dotIndex - 1;
}

function roundToStep(value: number, step: number, min: number): number {
  const rounded = Math.round((value - min) / step) * step + min;
  const precision = getDecimalPrecision(step);

  return Number(rounded.toFixed(precision));
}

export function Knob({
  value,
  defaultValue = 0,
  onValueChange,
  min = 0,
  max = 100,
  step = 1,
  size = "md",
  disabled = false,
  className,
  children,
}: KnobProps): JSX.Element {
  const controlRef = useRef<HTMLDivElement>(null);
  const labelId = useId();
  const { diameter, strokeWidth, thumbSize, boxSize } =
    getKnobSizeDimensions(size);

  const isControlled = value !== undefined;
  const [internalValue, setInternalValue] = useState(defaultValue);
  const currentValue = clamp(isControlled ? value : internalValue, min, max);

  function commit(nextValue: number) {
    const rounded = roundToStep(nextValue, step, min);
    const clamped = clamp(rounded, min, max);

    if (!isControlled) {
      setInternalValue(clamped);
    }

    onValueChange?.(clamped);
  }

  return (
    <KnobContext.Provider
      value={makeKnobContext({
        value: currentValue,
        min,
        max,
        step,
        disabled,
        labelId,
        diameter,
        strokeWidth,
        thumbSize,
        boxSize,
        controlRef,
        setValue: commit,
      })}
    >
      <div
        data-slot="knob"
        data-disabled={disabled || undefined}
        data-size={isSizePreset(size, SIZES) ? size : undefined}
        className={clsx(styles.root, className)}
      >
        {children}
      </div>
    </KnobContext.Provider>
  );
}
